import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Plus, Trash2, Wrench, AlertTriangle, History as HistoryIcon, CheckCircle2,
  Calendar as CalendarLucide, Gauge, CarFront,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { ptBR, enUS, es } from "date-fns/locale";

const DATE_LOCALES = { pt: ptBR, en: enUS, es };

const SERVICE_TYPES = [
  "oil_change", "tire_rotation", "tire_replacement", "brakes",
  "ma_inspection", "alignment", "transmission_fluid", "battery", "other",
];

const SEVERITY_META = {
  overdue: { bg: "bg-rose-50 border-rose-300", badge: "bg-rose-600 text-white", label: "overdue" },
  soon: { bg: "bg-amber-50 border-amber-300", badge: "bg-amber-500 text-white", label: "soon" },
  ok: { bg: "bg-zinc-50 border-zinc-200", badge: "bg-zinc-200 text-zinc-700", label: "ok" },
};

export default function Manutencao() {
  const { t, lang, fmtCurrency, fmtNumber } = useT();
  const dateLocale = DATE_LOCALES[lang] || enUS;
  const [tab, setTab] = useState("services");
  const [services, setServices] = useState([]);
  const [history, setHistory] = useState([]);
  const [accidents, setAccidents] = useState([]);
  const [cars, setCars] = useState([]);

  const load = async () => {
    const [s, h, a, c] = await Promise.all([
      api.get("/car-services"),
      api.get("/maintenance"),
      api.get("/accidents"),
      api.get("/cars"),
    ]);
    setServices(s.data || []);
    setHistory(h.data || []);
    setAccidents(a.data || []);
    setCars(c.data || []);
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="manutencao-page">
      <div>
        <div className="overline">{t("maintenanceV2.pageTitle")}</div>
        <p className="text-sm text-zinc-500 mt-1">{t("maintenanceV2.pageSubtitle")}</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-none p-0 bg-transparent border-b border-zinc-200 w-full justify-start">
          <TabsTrigger value="services" className="rounded-none data-[state=active]:bg-[#F5A623] data-[state=active]:text-white text-xs py-2.5 px-4" data-testid="tab-services">
            <Wrench className="w-3.5 h-3.5 mr-1.5" /> {t("maintenanceV2.tabs.services")}
          </TabsTrigger>
          <TabsTrigger value="history" className="rounded-none data-[state=active]:bg-[#F5A623] data-[state=active]:text-white text-xs py-2.5 px-4" data-testid="tab-history">
            <HistoryIcon className="w-3.5 h-3.5 mr-1.5" /> {t("maintenanceV2.tabs.history")}
          </TabsTrigger>
          <TabsTrigger value="accidents" className="rounded-none data-[state=active]:bg-[#F5A623] data-[state=active]:text-white text-xs py-2.5 px-4" data-testid="tab-accidents">
            <AlertTriangle className="w-3.5 h-3.5 mr-1.5" /> {t("maintenanceV2.tabs.accidents")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="services" className="mt-4">
          <ServicesPanel services={services} cars={cars} reload={load} dateLocale={dateLocale} t={t} fmtNumber={fmtNumber} />
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <HistoryPanel history={history} cars={cars} reload={load} dateLocale={dateLocale} t={t} fmtCurrency={fmtCurrency} fmtNumber={fmtNumber} />
        </TabsContent>

        <TabsContent value="accidents" className="mt-4">
          <AccidentsPanel accidents={accidents} cars={cars} reload={load} dateLocale={dateLocale} t={t} fmtCurrency={fmtCurrency} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============ Services panel ============

function ServicesPanel({ services, cars, reload, dateLocale, t, fmtNumber }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ car_id: "", service_type: "oil_change", last_done_date: "", last_done_miles: "", interval_miles: "", interval_months: "", notes: "" });
  const [performing, setPerforming] = useState(null);
  const [perfForm, setPerfForm] = useState({ cost: 0, mileage: "", notes: "" });

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        last_done_miles: form.last_done_miles ? Number(form.last_done_miles) : null,
        interval_miles: form.interval_miles ? Number(form.interval_miles) : null,
        interval_months: form.interval_months ? Number(form.interval_months) : null,
        last_done_date: form.last_done_date ? new Date(form.last_done_date).toISOString() : null,
      };
      await api.post("/car-services", payload);
      toast.success(t("maintenanceV2.serviceCreated"));
      setOpen(false);
      setForm({ car_id: "", service_type: "oil_change", last_done_date: "", last_done_miles: "", interval_miles: "", interval_months: "", notes: "" });
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("maintenanceV2.confirmDeleteService"))) return;
    try {
      await api.delete(`/car-services/${id}`);
      toast.success(t("maintenanceV2.serviceDeleted"));
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const performService = async (svc) => {
    try {
      const params = new URLSearchParams({
        cost: String(perfForm.cost || 0),
      });
      if (perfForm.mileage) params.set("mileage", String(perfForm.mileage));
      if (perfForm.notes) params.set("notes", perfForm.notes);
      await api.post(`/car-services/${svc.id}/perform?${params.toString()}`);
      toast.success(t("maintenanceV2.servicePerformed"));
      setPerforming(null);
      setPerfForm({ cost: 0, mileage: "", notes: "" });
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <>
      <div className="flex justify-end mb-3">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-none bg-[#F5A623] hover:bg-[#D97706]" data-testid="add-service-button">
              <Plus className="w-4 h-4 mr-2" /> {t("maintenanceV2.addService")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl rounded-none">
            <DialogHeader>
              <DialogTitle className="font-display tracking-tight">{t("maintenanceV2.addService")}</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-2 gap-4">
              <Field label={t("rentals.vehicle")} full>
                <Select value={form.car_id} onValueChange={(v) => setForm({ ...form, car_id: v })}>
                  <SelectTrigger className="rounded-none"><SelectValue placeholder={t("common.select")} /></SelectTrigger>
                  <SelectContent>
                    {cars.map((c) => <SelectItem key={c.id} value={c.id}>{c.model} — {c.plate}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label={t("maintenanceV2.serviceType")} full>
                <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}>
                  <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SERVICE_TYPES.map((st) => <SelectItem key={st} value={st}>{t(`maintenanceV2.types.${st}`)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label={t("maintenanceV2.lastDoneDate")}>
                <Input type="date" className="rounded-none" value={form.last_done_date}
                  onChange={(e) => setForm({ ...form, last_done_date: e.target.value })} />
              </Field>
              <Field label={t("maintenanceV2.lastDoneMiles")}>
                <Input type="number" className="rounded-none" value={form.last_done_miles}
                  onChange={(e) => setForm({ ...form, last_done_miles: e.target.value })} />
              </Field>
              <Field label={t("maintenanceV2.intervalMiles")}>
                <Input type="number" className="rounded-none" value={form.interval_miles}
                  onChange={(e) => setForm({ ...form, interval_miles: e.target.value })}
                  placeholder={t("maintenanceV2.intervalMilesHint")} />
              </Field>
              <Field label={t("maintenanceV2.intervalMonths")}>
                <Input type="number" className="rounded-none" value={form.interval_months}
                  onChange={(e) => setForm({ ...form, interval_months: e.target.value })}
                  placeholder={t("maintenanceV2.intervalMonthsHint")} />
              </Field>
              <Field label={t("common.notes")} full>
                <Input className="rounded-none" value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </Field>
              <DialogFooter className="col-span-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none">{t("common.cancel")}</Button>
                <Button type="submit" className="rounded-none bg-[#F5A623] hover:bg-[#D97706]" data-testid="save-service-button">{t("common.save")}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {services.length === 0 ? (
        <div className="border border-zinc-200 p-16 text-center">
          <Wrench className="w-12 h-12 mx-auto text-zinc-300 mb-4" />
          <div className="overline mb-2">{t("maintenanceV2.emptyServices")}</div>
          <p className="text-sm text-zinc-500">{t("maintenanceV2.emptyServicesSub")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {services.map((svc) => {
            const sev = SEVERITY_META[svc.severity] || SEVERITY_META.ok;
            return (
              <div key={svc.id} className={`border ${sev.bg} p-4 space-y-2.5`} data-testid="service-card">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-xs font-mono uppercase text-zinc-500 mb-0.5 flex items-center gap-1">
                      <CarFront className="w-3 h-3" /> {svc.car?.model} · {svc.car?.plate}
                    </div>
                    <div className="font-display font-semibold tracking-tight text-lg leading-tight">
                      {svc.custom_label || t(`maintenanceV2.types.${svc.service_type}`)}
                    </div>
                  </div>
                  <span className={`text-[10px] font-mono px-2 py-1 ${sev.badge}`}>
                    {t(`maintenanceV2.severity.${sev.label}`)}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-white border border-zinc-200 px-2 py-1.5">
                    <div className="overline text-[10px]">{t("maintenanceV2.daysLeft")}</div>
                    <div className="font-mono font-semibold">{svc.days_left !== null && svc.days_left !== undefined ? svc.days_left : "—"}</div>
                  </div>
                  <div className="bg-white border border-zinc-200 px-2 py-1.5">
                    <div className="overline text-[10px]">{t("maintenanceV2.milesLeft")}</div>
                    <div className="font-mono font-semibold">{svc.miles_left !== null && svc.miles_left !== undefined ? fmtNumber(svc.miles_left) : "—"}</div>
                  </div>
                </div>

                <div className="text-xs text-zinc-600 space-y-0.5">
                  {svc.last_done_date && (
                    <div className="flex items-center gap-1.5"><CalendarLucide className="w-3 h-3" /> {t("maintenanceV2.last")}: {format(parseISO(svc.last_done_date), "dd MMM yyyy", { locale: dateLocale })}</div>
                  )}
                  {svc.last_done_miles !== null && svc.last_done_miles !== undefined && (
                    <div className="flex items-center gap-1.5"><Gauge className="w-3 h-3" /> {fmtNumber(svc.last_done_miles)} mi</div>
                  )}
                </div>

                <div className="flex gap-2 pt-2 border-t border-zinc-200">
                  <Button size="sm" onClick={() => setPerforming(svc)}
                    className="rounded-none bg-green-600 hover:bg-green-700 text-white flex-1 h-8 text-xs"
                    data-testid="perform-service-button">
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> {t("maintenanceV2.markPerformed")}
                  </Button>
                  <button onClick={() => remove(svc.id)} className="p-1.5 border border-zinc-200 hover:border-red-400 hover:text-red-600">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Mark performed dialog */}
      <Dialog open={!!performing} onOpenChange={(o) => !o && setPerforming(null)}>
        <DialogContent className="max-w-md rounded-none">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              {t("maintenanceV2.performTitle", { name: performing?.custom_label || t(`maintenanceV2.types.${performing?.service_type}`) })}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Field label={t("maintenance.cost")}>
              <Input type="number" step="0.01" className="rounded-none" value={perfForm.cost}
                onChange={(e) => setPerfForm({ ...perfForm, cost: e.target.value })}
                data-testid="perf-cost-input" />
            </Field>
            <Field label={t("maintenance.mileageAt")}>
              <Input type="number" className="rounded-none" value={perfForm.mileage}
                onChange={(e) => setPerfForm({ ...perfForm, mileage: e.target.value })}
                placeholder={performing?.current_mileage?.toString()} />
            </Field>
            <Field label={t("common.notes")}>
              <Input className="rounded-none" value={perfForm.notes}
                onChange={(e) => setPerfForm({ ...perfForm, notes: e.target.value })} />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPerforming(null)} className="rounded-none">{t("common.cancel")}</Button>
            <Button onClick={() => performService(performing)} className="rounded-none bg-green-600 hover:bg-green-700 text-white" data-testid="confirm-perform-button">
              {t("maintenanceV2.markPerformed")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============ History panel (existing /api/maintenance) ============

function HistoryPanel({ history, cars, reload, dateLocale, t, fmtCurrency, fmtNumber }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ car_id: "", service_type: "", cost: 0, mileage: "", performed_at: new Date().toISOString().slice(0, 10), notes: "" });

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/maintenance", {
        ...form,
        cost: Number(form.cost),
        mileage: form.mileage ? Number(form.mileage) : null,
        performed_at: new Date(form.performed_at).toISOString(),
        next_service_at: null,
      });
      toast.success(t("maintenance.created"));
      setOpen(false);
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("maintenance.confirmDelete"))) return;
    try {
      await api.delete(`/maintenance/${id}`);
      toast.success(t("maintenance.deleted"));
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const total = history.reduce((a, m) => a + Number(m.cost || 0), 0);

  return (
    <>
      <div className="flex justify-between items-center mb-3">
        <div className="overline">{t("maintenance.pageTitle", { n: history.length, cost: fmtCurrency(total) })}</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-none bg-[#F5A623] hover:bg-[#D97706]" data-testid="add-history-button">
              <Plus className="w-4 h-4 mr-2" /> {t("maintenance.newMaintenance")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl rounded-none">
            <DialogHeader><DialogTitle className="font-display tracking-tight">{t("maintenance.newTitle")}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-2 gap-4">
              <Field label={t("rentals.vehicle")} full>
                <Select value={form.car_id} onValueChange={(v) => setForm({ ...form, car_id: v })}>
                  <SelectTrigger className="rounded-none"><SelectValue placeholder={t("common.select")} /></SelectTrigger>
                  <SelectContent>{cars.map((c) => <SelectItem key={c.id} value={c.id}>{c.model} — {c.plate}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label={t("maintenance.serviceType")} full>
                <Input className="rounded-none" required value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })} placeholder={t("maintenance.servicePlaceholder")} />
              </Field>
              <Field label={t("maintenance.cost")}>
                <Input type="number" step="0.01" className="rounded-none" required value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} />
              </Field>
              <Field label={t("maintenance.mileageAt")}>
                <Input type="number" className="rounded-none" value={form.mileage} onChange={(e) => setForm({ ...form, mileage: e.target.value })} />
              </Field>
              <Field label={t("maintenance.performedAt")} full>
                <Input type="date" className="rounded-none" value={form.performed_at} onChange={(e) => setForm({ ...form, performed_at: e.target.value })} />
              </Field>
              <Field label={t("common.notes")} full>
                <Input className="rounded-none" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </Field>
              <DialogFooter className="col-span-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none">{t("common.cancel")}</Button>
                <Button type="submit" className="rounded-none bg-[#F5A623] hover:bg-[#D97706]">{t("common.save")}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {history.length === 0 ? (
        <div className="border border-zinc-200 p-16 text-center">
          <HistoryIcon className="w-12 h-12 mx-auto text-zinc-300 mb-4" />
          <div className="overline mb-2">{t("maintenance.emptyTitle")}</div>
        </div>
      ) : (
        <div className="border border-zinc-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left">
                <Th>{t("common.date")}</Th><Th>{t("rentals.vehicle")}</Th><Th>{t("maintenance.service")}</Th>
                <Th>{t("maintenance.miles")}</Th><Th>{t("maintenance.cost")}</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {history.map((m) => (
                <tr key={m.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <Td className="font-mono text-xs">{format(parseISO(m.performed_at), "dd MMM yyyy", { locale: dateLocale })}</Td>
                  <Td className="font-medium">{m.car?.model} <span className="text-xs text-zinc-500 font-mono">({m.car?.plate})</span></Td>
                  <Td>{m.service_type}</Td>
                  <Td className="font-mono text-xs">{m.mileage ? fmtNumber(m.mileage) : "—"}</Td>
                  <Td className="font-mono font-semibold text-red-700">{fmtCurrency(m.cost)}</Td>
                  <Td><button onClick={() => remove(m.id)} className="p-1.5 border border-zinc-200 hover:border-red-400 hover:text-red-600 ml-auto block"><Trash2 className="w-3 h-3" /></button></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ============ Accidents panel ============

function AccidentsPanel({ accidents, cars, reload, dateLocale, t, fmtCurrency }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    car_id: "", incident_date: new Date().toISOString().slice(0, 10),
    location: "", description: "", estimated_cost: 0,
    police_report_number: "", insurance_claim_number: "",
    third_party_involved: false, third_party_info: "",
    status: "open", notes: "",
  });

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/accidents", {
        ...form,
        estimated_cost: Number(form.estimated_cost),
        incident_date: new Date(form.incident_date).toISOString(),
      });
      toast.success(t("maintenanceV2.accidentCreated"));
      setOpen(false);
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("maintenanceV2.confirmDeleteAccident"))) return;
    try {
      await api.delete(`/accidents/${id}`);
      toast.success(t("maintenanceV2.accidentDeleted"));
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <>
      <div className="flex justify-end mb-3">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-none bg-rose-600 hover:bg-rose-700 text-white" data-testid="add-accident-button">
              <Plus className="w-4 h-4 mr-2" /> {t("maintenanceV2.addAccident")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl rounded-none">
            <DialogHeader><DialogTitle className="font-display tracking-tight">{t("maintenanceV2.addAccident")}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-2 gap-4">
              <Field label={t("rentals.vehicle")} full>
                <Select value={form.car_id} onValueChange={(v) => setForm({ ...form, car_id: v })}>
                  <SelectTrigger className="rounded-none"><SelectValue placeholder={t("common.select")} /></SelectTrigger>
                  <SelectContent>{cars.map((c) => <SelectItem key={c.id} value={c.id}>{c.model} — {c.plate}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label={t("maintenanceV2.incidentDate")}>
                <Input type="date" className="rounded-none" required value={form.incident_date} onChange={(e) => setForm({ ...form, incident_date: e.target.value })} />
              </Field>
              <Field label={t("maintenanceV2.estimatedCost")}>
                <Input type="number" step="0.01" className="rounded-none" value={form.estimated_cost} onChange={(e) => setForm({ ...form, estimated_cost: e.target.value })} />
              </Field>
              <Field label={t("tolls.location")} full>
                <Input className="rounded-none" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
              </Field>
              <Field label={t("common.description")} full>
                <Input className="rounded-none" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="accident-description-input" />
              </Field>
              <Field label={t("maintenanceV2.policeReport")}>
                <Input className="rounded-none" value={form.police_report_number} onChange={(e) => setForm({ ...form, police_report_number: e.target.value })} />
              </Field>
              <Field label={t("maintenanceV2.insuranceClaim")}>
                <Input className="rounded-none" value={form.insurance_claim_number} onChange={(e) => setForm({ ...form, insurance_claim_number: e.target.value })} />
              </Field>
              <Field label={t("maintenanceV2.thirdPartyInfo")} full>
                <Input className="rounded-none" value={form.third_party_info} onChange={(e) => setForm({ ...form, third_party_info: e.target.value, third_party_involved: !!e.target.value })} placeholder={t("maintenanceV2.thirdPartyHint")} />
              </Field>
              <Field label={t("common.notes")} full>
                <Input className="rounded-none" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </Field>
              <DialogFooter className="col-span-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none">{t("common.cancel")}</Button>
                <Button type="submit" className="rounded-none bg-rose-600 hover:bg-rose-700 text-white" data-testid="save-accident-button">{t("common.save")}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {accidents.length === 0 ? (
        <div className="border border-zinc-200 p-16 text-center">
          <AlertTriangle className="w-12 h-12 mx-auto text-zinc-300 mb-4" />
          <div className="overline mb-2">{t("maintenanceV2.emptyAccidents")}</div>
        </div>
      ) : (
        <div className="border border-zinc-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left">
                <Th>{t("maintenanceV2.incidentDate")}</Th>
                <Th>{t("rentals.vehicle")}</Th>
                <Th>{t("common.description")}</Th>
                <Th>{t("tolls.location")}</Th>
                <Th>{t("maintenanceV2.estimatedCost")}</Th>
                <Th>{t("common.status")}</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {accidents.map((a) => (
                <tr key={a.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <Td className="font-mono text-xs">{format(parseISO(a.incident_date), "dd MMM yyyy", { locale: dateLocale })}</Td>
                  <Td className="font-medium">{a.car?.model} <span className="text-xs text-zinc-500 font-mono">({a.car?.plate})</span></Td>
                  <Td className="max-w-sm">{a.description}</Td>
                  <Td className="text-xs">{a.location || "—"}</Td>
                  <Td className="font-mono font-semibold text-rose-700">{fmtCurrency(a.estimated_cost)}</Td>
                  <Td><span className="text-xs font-mono">{t(`maintenanceV2.accidentStatus.${a.status}`)}</span></Td>
                  <Td><button onClick={() => remove(a.id)} className="p-1.5 border border-zinc-200 hover:border-red-400 hover:text-red-600 ml-auto block"><Trash2 className="w-3 h-3" /></button></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

const Th = ({ children }) => <th className="px-4 py-3 overline">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
const Field = ({ label, children, full }) => (
  <div className={`space-y-1.5 ${full ? "col-span-2" : ""}`}>
    <label className="overline">{label}</label>
    {children}
  </div>
);
